import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceArea } from "recharts";
import { TrendUp, TrendDown, CheckCircle, Warning, WarningCircle, Info } from "@phosphor-icons/react";
import { METRICS, metricStats, metricVerdict, trendGood } from "./metricDefs";

const levelIcon = {
  success: <CheckCircle size={20} weight="fill" className="text-[#00e676] shrink-0" />,
  warning: <Warning size={20} weight="fill" className="text-[#ffea00] shrink-0" />,
  alert: <WarningCircle size={20} weight="fill" className="text-[#ff3b30] shrink-0" />,
  info: <Info size={20} weight="fill" className="text-primary shrink-0" />,
};
const fmtDate = (d) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;

export default function MetricDetailSheet({ metricKey, days, onClose }) {
  const m = metricKey ? METRICS[metricKey] : null;
  const stats = m ? metricStats(days, metricKey) : null;
  const verdict = m && stats ? metricVerdict(metricKey, stats) : null;
  const trend = stats ? trendGood(metricKey, stats.deltaPct) : "neutral";
  const trendColor = trend === "good" ? "text-[#00e676]" : trend === "bad" ? "text-[#ff3b30]" : "text-muted-foreground";

  return (
    <Sheet open={!!metricKey} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="glass-modal w-full sm:max-w-xl overflow-y-auto" data-testid="metric-detail-sheet">
        {m && stats && (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading text-2xl">{m.label}</SheetTitle>
              <SheetDescription className="text-muted-foreground">История, сравнение с вашей базой и вердикт</SheetDescription>
            </SheetHeader>
            <div className="mt-4 flex items-end gap-3">
              <span className="font-heading text-5xl font-bold num" style={{ color: m.color }} data-testid="metric-detail-value">
                {Math.round(stats.current * 10) / 10}
              </span>
              <span className="text-sm text-muted-foreground mb-1.5">{m.unit}</span>
              {Math.abs(stats.deltaPct) >= 1 && (
                <span className={`flex items-center gap-1 text-sm num mb-1.5 ${trendColor}`} data-testid="metric-detail-trend">
                  {stats.deltaPct > 0 ? <TrendUp size={16} /> : <TrendDown size={16} />}
                  {Math.abs(stats.deltaPct).toFixed(0)}% от базы
                </span>
              )}
            </div>

            <div className="mt-5 rounded-lg border border-border/60 bg-background/40 p-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">История · зона нормы ±2σ</div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={days}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 10% 16%)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="hsl(220 10% 40%)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis domain={m.domain || ["auto", "auto"]} stroke="hsl(220 10% 40%)" fontSize={11} tickLine={false} axisLine={false} width={35} />
                  <Tooltip contentStyle={{ background: "hsl(220 10% 12%)", border: "1px solid hsl(220 10% 22%)", borderRadius: 8, fontSize: 12 }} labelFormatter={fmtDate} />
                  <ReferenceArea y1={stats.lo} y2={stats.hi} fill={m.color} fillOpacity={0.07} />
                  <ReferenceLine y={stats.avg} stroke={m.color} strokeDasharray="5 5" strokeOpacity={0.6}
                    label={{ value: "база", fill: "hsl(220 10% 60%)", fontSize: 10, position: "insideTopRight" }} />
                  <Area type="monotone" dataKey={metricKey} name={m.label} stroke={m.color} strokeWidth={2} fill={m.color} fillOpacity={0.15} connectNulls />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              {[["Текущее", stats.current], ["Средняя база", stats.avg], ["Минимум", stats.min], ["Максимум", stats.max]].map(([l, v]) => (
                <div key={l} className="rounded-md bg-secondary/60 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{l}</div>
                  <div className="num text-sm font-semibold">{Math.round(v * 10) / 10}</div>
                </div>
              ))}
            </div>

            {verdict && (
              <div className="mt-4 rounded-lg border border-border/60 p-4 flex gap-3" data-testid="metric-detail-verdict">
                {levelIcon[verdict.level]}
                <div>
                  <div className="text-sm font-semibold mb-1">Вердикт</div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{verdict.text}</p>
                </div>
              </div>
            )}

            <div className="mt-4">
              <div className="text-sm font-semibold mb-1.5">Что это такое</div>
              <p className="text-xs text-muted-foreground leading-relaxed" data-testid="metric-detail-desc">{m.desc}</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
